import { useState } from "react";
import { useSignIn } from "@clerk/clerk-react";
import { useNavigate, Link } from "react-router-dom";
import { GOOGLE_OAUTH_ENABLED } from "../../lib/feature-flags";

export default function LoginPage() {
  const { signIn, isLoaded, setActive } = useSignIn();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded) return;
    setLoading(true);
    setError("");

    try {
      const result = await signIn.create({
        identifier: email,
        password,
      });

      if (result.status === "complete") {
        await setActive({ session: result.createdSessionId });
        navigate("/");
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.longMessage || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-surface flex items-center justify-center p-4">
      <div className="bg-brand-bg rounded-card shadow-card w-full max-w-4xl flex flex-col md:flex-row">
        {/* Left: Login Form */}
        <div className="flex-1 p-8 md:p-12">
          <h1 className="font-thin text-4xl md:text-5xl text-brand-text mb-8">
            Votre compte
          </h1>

          {GOOGLE_OAUTH_ENABLED && (
            <>
              {/* Google OAuth */}
              <button
                type="button"
                onClick={() =>
                  signIn?.authenticateWithRedirect({
                    strategy: "oauth_google",
                    redirectUrl: "/sso-callback",
                    redirectUrlComplete: "/",
                  })
                }
                disabled={!isLoaded}
                className="w-full flex items-center justify-center gap-3 px-6 py-3 rounded-pill border border-brand-border text-brand-text font-bold text-sm hover:bg-brand-surface transition-colors disabled:opacity-50"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continuer avec Google
              </button>

              <div className="flex items-center gap-4 my-2">
                <div className="flex-1 h-px bg-brand-border" />
                <span className="text-brand-muted text-xs uppercase tracking-wide">ou</span>
                <div className="flex-1 h-px bg-brand-border" />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Courriel"
              className="w-full px-6 py-3 rounded-pill border border-brand-primary text-brand-text placeholder-brand-muted bg-brand-bg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              required
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe"
              className="w-full px-6 py-3 rounded-pill border border-brand-primary text-brand-text placeholder-brand-muted bg-brand-bg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              required
            />

            {error && (
              <p className="text-brand-error text-sm">{error}</p>
            )}

            <div className="flex items-center gap-6 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 rounded-pill border border-brand-primary text-brand-primary font-black text-sm uppercase tracking-wide hover:bg-brand-primary hover:text-brand-surface transition-colors disabled:opacity-50"
              >
                Me connecter
              </button>
              <Link
                to="/forgot-password"
                className="text-sm text-brand-text hover:text-brand-primary"
              >
                J&apos;ai oublié mon mot de passe
              </Link>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
}
